import { useEffect, useState, type ReactNode } from "react";
import { AUTH_EXPIRED_EVENT, fetchAuthConfig, getToken, handleRedirectCallback, ensureFreshToken, login, getLocalUser, setLocalUser, isOktaConfigured, notifyAuthExpired, takeReturnTo } from "../lib/auth";
import SessionExpiryBanner from "./SessionExpiryBanner";
import { flushBeforeExpiry } from "../lib/sessionExpiry";
import Wordmark from "./Wordmark";

// Top-level auth gate. The app gates itself, not an edge/ALB OIDC gate, so
// the SPA must not render the app — nor fire its data/SSE calls (useHub) — until
// the user has an identity. Mounted around <App/> in main.tsx. The screen reuses
// the app's chrome (TopBar wordmark + tool-name) and panel styling so signed-out
// and signed-in look like one product.
//
//   loading → still resolving the session / handling the Okta redirect
//   ok      → has an identity (Okta token, or a local sign-in off-Okta)
//   signin  → Okta enabled but no token → "Sign in with Okta"
//   local   → Okta NOT configured and no local identity → default Omni Hub sign-in
type Status = "loading" | "ok" | "signin" | "local";

export default function AuthGate({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [authError, setAuthError] = useState<string | null>(null);
  // The /api/auth/config fetch failed (fetchAuthConfig fails CLOSED): we can't
  // start a PKCE login without the issuer, so the sign-in button reloads instead.
  const [configBroken, setConfigBroken] = useState(false);

  useEffect(() => {
    void (async () => {
      // If we're returning from Okta with a ?code (or ?error), process it first.
      const cb = await handleRedirectCallback();
      if (cb.error) setAuthError(cb.error);
      // Reauthenticating mid-edit used to land the writer at the app root, with
      // their draft safe on the server and nowhere in sight (ENG-54731). Okta can
      // only return to a registered redirect URI, so the path was parked before
      // the redirect; put it back before anything renders. replaceState, not a
      // navigate: this is restoring where they already were, not a new entry.
      if (cb.ok) {
        const back = takeReturnTo();
        if (back && back !== window.location.pathname + window.location.search) {
          window.history.replaceState(null, "", back);
        }
      }
      const cfg = await fetchAuthConfig();
      if (!cfg.enabled) {
        // Off-Okta: open gate, but name yourself first (default Omni Hub sign-in).
        setStatus(getLocalUser() ? "ok" : "local");
        return;
      }
      if (!cfg.issuer || !cfg.clientId) {
        // Config unreachable — stay on the sign-in screen, never fall open.
        setConfigBroken(true);
        setAuthError((e) => e ?? "Couldn't reach the Omni Hub service to load sign-in. Try again shortly.");
        setStatus("signin");
        return;
      }
      await ensureFreshToken();
      setStatus(getToken() ? "ok" : "signin");
    })();
  }, []);

  // A session lapsed (a data call 401'd, or the proactive check below found the
  // token expired): drop the app immediately and return to the sign-in screen.
  useEffect(() => {
    const onExpired = () => {
      setAuthError("Your session has expired. Please sign in again.");
      setStatus((s) => (s === "ok" ? "signin" : s));
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  // While signed in, keep the token fresh so getToken() stays synchronous — and
  // if the session has already lapsed past what a refresh can recover (the tab
  // slept, or there's no in-memory refresh token after a reload), proactively
  // surface the sign-in prompt instead of waiting for the next data call to
  // 401. Only in Okta mode: off-Okta the gate is open and there is no token to
  // expire, so this must never bounce the user to a sign-in screen. Runs on a
  // timer plus on focus/visibility so a long-idle tab prompts as soon as it's
  // brought back.
  useEffect(() => {
    if (status !== "ok") return;
    const check = () =>
      void (async () => {
        await ensureFreshToken();
        if (!isOktaConfigured() || getToken()) return;
        // Save before the app comes down. notifyAuthExpired() unmounts children,
        // which is precisely how an editor's unsaved work used to disappear — so
        // the flush has to complete first, even though by this point the token is
        // already gone and the save may well fail. The banner's own expiry path
        // gets there sooner and with a valid token; this is the backstop for a
        // tab that was asleep through the whole warning window.
        await flushBeforeExpiry();
        notifyAuthExpired();
      })();
    const id = setInterval(check, 60_000);
    const onFocus = () => check();
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [status]);

  function submitLocal(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setLocalUser({ name, email });
    setStatus("ok");
  }

  // The banner sits above the app on EVERY page rather than inside any one
  // surface — the criterion is that the warning is consistent everywhere, and a
  // per-page banner is the same code copied into each, missing from whichever
  // ships next.
  if (status === "ok") {
    return (
      <>
        <SessionExpiryBanner />
        {children}
      </>
    );
  }

  return (
    <div className="auth-screen">
      <div className="auth-body">
        <div className="auth-card">
          <div className="auth-brand">
            <span className="wordmark" aria-label="Incode">
              <Wordmark />
            </span>
            <span className="tool-name">Omni Hub</span>
          </div>

          {status === "loading" && (
            <>
              <h1 className="auth-title">Welcome</h1>
              <div className="auth-sub">Checking your session…</div>
            </>
          )}

          {status === "signin" && (
            <>
              <h1 className="auth-title">Sign in</h1>
              {authError ? (
                <div className="auth-alert" role="alert">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><line x1="12" y1="8" x2="12" y2="13" /><line x1="12" y1="16.5" x2="12" y2="16.5" /></svg>
                  <span>{authError}</span>
                </div>
              ) : (
                <div className="auth-sub">Use your Incode Okta account to continue.</div>
              )}
              <button
                className="auth-btn primary block"
                onClick={() => {
                  // Without the issuer (config fetch failed) a PKCE login can't
                  // start — reload to retry the config instead of a dead click.
                  if (configBroken) window.location.reload();
                  else void login();
                }}
              >
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" /></svg>
                Sign in with Okta
              </button>
            </>
          )}

          {status === "local" && (
            <>
              <h1 className="auth-title">Sign in</h1>
              <div className="auth-sub">Enter your name to continue.</div>
              <form onSubmit={submitLocal} className="auth-form">
                <input
                  className="auth-input"
                  placeholder="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                  aria-label="Name"
                />
                <input
                  className="auth-input"
                  placeholder="Email (optional)"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-label="Email (optional)"
                />
                <button className="auth-btn primary block" type="submit" disabled={!name.trim()}>
                  Continue
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
